import {
    useCallback, useState, useEffect, useMemo,
} from 'react'
import clsx from 'clsx'
import isEmpty from 'lodash/isEmpty'
import Button from 'app/shared-components/Button'
import useRequestTable from 'app/hooks/useRequestTable'
import {
    AccountLanesTable,
} from 'app/Apps/AccountManagement/AccountLanes/AccountLanes.types'
import {
    RequestKeys,
} from 'app/hooks/useRequest'
import requests from 'app/requests/Lane/Lane.requests'
import StatusHandler from 'app/shared-components/StatusHandler'
import useAQuoteInformationContext from '../AQuoteCreate/AQuoteInformationContext'
import useStyles from '../AQuoteCreate/AQuoteCreate.styles'
import {
    AQuote, RequestedLane,
} from '../AQuotes.types'
import QuotationOptionsTable from './QuotationOptionsTable'
import {
    QuotationOptionsTableType,
} from './QuotationOptionsTable/QuotationOptionsTable.config'
import {
    parseBaseLeaseDays,
} from './QuotationOptions.utils'

export default function QuotationOptions({
    actionQuote,
    data: aQuote,
}: Readonly<{
    actionQuote: (action: AQuote) => void,
    data: AQuote
}>) {
    const {
        classes,
    } = useStyles()

    const {
        isEdited,
        setIsEdited,
    } = useAQuoteInformationContext()

    const [
        pending,
        setPending,
    ] = useState<boolean>(false)

    const [
        value,
        setValue,
    ] = useState<QuotationOptionsTableType[]>()

    const {
        isDataReady,
        data: lanes,
        isFetching,
        isError,
        error,
    } = useRequestTable<{ data: AccountLanesTable[] }>({
        key: RequestKeys.getLanesByQuote,
        params: {},
        defaultValue: {
            data: {
                filters: {
                    leaseType: aQuote?.leaseType,
                    quoteId: aQuote?.id,
                    accountId: aQuote?.account?.id,
                },
            },
            sort: {
                field: 'laneNumber',
                direction: 'asc',
            },
        },
        config: requests.getLanesByQuote,
        keepPreviousData: true,
        enabled: !isEmpty(aQuote),
    })

    useEffect(() => {
        if (isDataReady && isEmpty(value)) {
            setValue((lanes?.data || []).map((lane) => {
                const requestedLane = aQuote?.requestedLanes?.find((reqLane) => {
                    return reqLane?.accountLane?.id === lane?.id
                })

                return {
                    ...lane,
                    ...requestedLane,
                    id: lane?.id || null,
                    requestedLaneId: requestedLane?.id,
                    selected: Boolean(requestedLane?.id),
                } as QuotationOptionsTableType
            }))
        }
    }, [
        aQuote?.requestedLanes,
        isDataReady,
        lanes?.data,
        value,
    ])

    const {
        activeStep, setActiveStep, initialValue,
    } = useAQuoteInformationContext()

    const isCreate = !initialValue

    const handleChange = useCallback((updates: { id: number } & Record<string, string>) => {
        setIsEdited(true)
        setValue((prev) => {
            return [...prev.map((lane) => {
                const {
                    id, ...restUpdates
                } = updates

                if (id === lane.id) {
                    return {
                        ...lane,
                        ...restUpdates,
                    }
                }
                return lane
            })]
        })
    }, [setIsEdited])

    const toggleAll = useCallback((toggleValue: boolean) => {
        setValue((prev) => {
            return [...prev.map((row) => {
                return {
                    ...row,
                    selected: Boolean(row?.requestedLaneId) || toggleValue,
                }
            })]
        })
    }, [])

    const nextAction = useCallback(() => {
        setPending(true)
        actionQuote({
            ...aQuote,
            requestedLanes: value.reduce((acc, row) => {
                if (row.selected) {
                    return [
                        ...acc,
                        {
                            id: row?.requestedLaneId,
                            accountLane: {
                                id: row?.id,
                            },
                            baseLeaseDays: parseBaseLeaseDays(row?.baseLeaseDays),
                            productTypes: row?.productTypes,
                            otherComments: row?.otherComments,
                        } as RequestedLane,
                    ]
                }
                return acc
            }, [] as RequestedLane[]),
        })

        setPending(false)
    }, [
        actionQuote,
        aQuote,
        value,
    ])

    const backAction = useCallback(() => {
        setIsEdited(false)
        setActiveStep(activeStep - 1)
    }, [
        setIsEdited,
        setActiveStep,
        activeStep,
    ])

    const countSelected = useMemo(() => {
        return value?.filter(({
            selected,
            requestedLaneId,
        }) => {
            return selected && !requestedLaneId
        })
    }, [value])

    const renderButtonPanel = useCallback(({
        isValid,
        disabled,
    }) => {
        return (
            <div
                className={clsx(classes.buttonWrapper, classes.doubleButtons)}
            >
                <Button
                    label={isCreate ? 'Create' : 'Update'}
                    onClick={nextAction}
                    className={classes.button}
                    disabled={!isValid
                        || pending || disabled || !isEdited || !countSelected?.length}
                />
                <Button
                    label="Back"
                    secondary
                    onClick={backAction}
                    className={classes.button}
                />
            </div>
        )
    }, [
        classes.buttonWrapper,
        classes.doubleButtons,
        classes.button,
        isCreate,
        nextAction,
        pending,
        isEdited,
        countSelected,
        backAction,
    ])

    return (
        <StatusHandler
            isSuccess={!!value}
            isFetching={isFetching}
            isError={isError}
            error={error}
        >
            <QuotationOptionsTable
                data={value}
                onChange={handleChange}
                renderButtonPanel={renderButtonPanel}
                toggleAll={toggleAll}
            />
        </StatusHandler>

    )
}
